// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { useRef, useState } from 'react';
import clsx from 'clsx';
import { SingleTabStopNavigationReset, useMergeRefs, useSingleTabStopNavigation, useUniqueId, } from '@cloudscape-design/component-toolkit/internal';
import InternalDropdown from '../../../dropdown/internal';
import InternalToken from '../../../token/internal';
import { getBreakpointValue } from '../../breakpoints';
import { useListFocusController } from '../../hooks/use-list-focus-controller';
import { KeyCode } from '../../keycode';
import { circleIndex } from '../../utils/circle-index';
import handleKey from '../../utils/handle-key';
import ButtonTrigger from '../button-trigger';
import styles from './styles.css.js';
import testUtilStyles from './test-classes/styles.css.js';
export function OverflowDropdown({ tokens, open, disabled, readOnly, expandToViewport, triggerRef, tokenDismissLabel, tokenOverflowAriaLabel, onOpenChange, onDismiss, }) {
    const dropdownId = useUniqueId('token-overflow-');
    const triggerObjectRef = useRef(null);
    const mergedTriggerRef = useMergeRefs(triggerRef, triggerObjectRef);
    const { tabIndex } = useSingleTabStopNavigation(triggerObjectRef);
    const [nextFocusIndex, setNextFocusIndex] = useState(null);
    const listRef = useListFocusController({
        nextFocusIndex,
        onFocusMoved: target => {
            target.focus();
            setNextFocusIndex(null);
        },
        listItemSelector: `.${styles['overflow-dropdown-item']}`,
    });
    const getItemButtons = () => {
        var _a, _b;
        return Array.from((_b = (_a = listRef.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll(`.${styles['overflow-dropdown-item']} button`)) !== null && _b !== void 0 ? _b : []);
    };
    const close = (returnFocus) => {
        var _a;
        onOpenChange(false);
        if (returnFocus) {
            (_a = triggerObjectRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
    };
    const focusItem = (index) => {
        var _a;
        const buttons = getItemButtons();
        (_a = buttons[circleIndex(index, [0, buttons.length - 1])]) === null || _a === void 0 ? void 0 : _a.focus();
    };
    const openAndFocus = (index) => {
        setNextFocusIndex(index);
        onOpenChange(true);
    };
    const onTriggerKeyDown = event => {
        if (event.detail.keyCode === KeyCode.down) {
            event.preventDefault();
            openAndFocus(0);
        }
        else if (event.detail.keyCode === KeyCode.up) {
            event.preventDefault();
            openAndFocus(tokens.length - 1);
        }
    };
    const dismiss = (index) => {
        if (tokens.length > 1) {
            setNextFocusIndex(index);
        }
        onDismiss(index);
    };
    const onListKeyDown = (event) => {
        const focusedIndex = getItemButtons().indexOf(document.activeElement);
        const dismissFocused = () => {
            event.preventDefault();
            if (focusedIndex !== -1 && !readOnly && !disabled) {
                dismiss(focusedIndex);
            }
        };
        handleKey(event, {
            onTab: () => close(false),
            onBackspace: dismissFocused,
            onDelete: dismissFocused,
            onBlockEnd: () => {
                event.preventDefault();
                focusItem(focusedIndex + 1);
            },
            onBlockStart: () => {
                event.preventDefault();
                focusItem(focusedIndex - 1);
            },
        });
    };
    return (React.createElement("div", { className: styles['token-overflow'] },
        React.createElement(InternalDropdown, { open: open, expandToViewport: expandToViewport, maxWidth: getBreakpointValue('xxs'), onEscape: () => close(true), onOutsideClick: () => close(false), trigger: React.createElement(ButtonTrigger, { ref: mergedTriggerRef, compact: true, tabIndex: tabIndex, disabled: disabled, pressed: open, className: clsx(styles['token-overflow-trigger'], testUtilStyles['token-overflow-trigger']), ariaHasPopup: false, ariaControls: open ? dropdownId : undefined, ariaLabel: tokenOverflowAriaLabel === null || tokenOverflowAriaLabel === void 0 ? void 0 : tokenOverflowAriaLabel(tokens.length), onClick: () => onOpenChange(!open), onKeyDown: onTriggerKeyDown }, `+${tokens.length}`), content: React.createElement(SingleTabStopNavigationReset, null,
                React.createElement("div", { ref: listRef, onKeyDown: onListKeyDown },
                    React.createElement("ul", { id: dropdownId, className: clsx(styles['overflow-dropdown'], testUtilStyles['overflow-dropdown']) }, tokens.map((token, index) => (React.createElement("li", { key: index, className: styles['overflow-dropdown-item'] },
                        React.createElement(InternalToken, { label: token.value, dismissLabel: tokenDismissLabel === null || tokenDismissLabel === void 0 ? void 0 : tokenDismissLabel(token.value), icon: token.icon, variant: "inline", disabled: disabled, readOnly: readOnly, onDismiss: () => dismiss(index) }))))))) })));
}
//# sourceMappingURL=overflow-dropdown.js.map